/**
 * True lazy-loads mega nav dropdown content via Shopify Section Rendering API.
 *
 * The initial page HTML contains only top-level nav items with placeholder divs.
 * An inline <script> in <head> starts the fetch (or reads sessionStorage) before
 * DOMContentLoaded — panels are injected as soon as data is ready, with no
 * dependency on user interaction.
 *
 * First visit: fetch starts in <head>, panels injected when it resolves.
 * Subsequent pages in session: HTML from sessionStorage, panels injected instantly
 * in init() before any user interaction is possible.
 *
 * nav-dropdown.liquid has the menu handle and mega-nav config hardcoded, so the
 * section works correctly when fetched from any page with no admin setup needed.
 *
 * Sidebar hover re-binding: MainMenu captures .js-sidebar-hover at init time so
 * injected elements are missed. A delegated capture-phase listener handles them.
 *
 * Cache invalidation: the storage key is provided by an inline script in
 * theme.liquid (window.__lazyNavKey) and is derived from the deployed asset
 * version, so every deploy busts the cache. sessionStorage survives browser
 * restarts when the previous session is restored, so a fixed key can pin a
 * browser to stale panels indefinitely. As a second line of defence, cached
 * panels are validated against the handles actually present in the header —
 * any mismatch discards the cache and refetches.
 */

(function () {
  'use strict';

  const SECTION_NAME = 'nav-dropdown';
  const SESSION_KEY = window.__lazyNavKey || 'lazy-nav-v2';
  const PLACEHOLDER_SELECTOR = '.lazy-nav__placeholder';
  const NAV_SELECTOR = '.main-nav';

  const panelCache = new Map();
  let fetchPromise = null;

  function parseHtml(html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    doc.querySelectorAll('[data-nav-handle]').forEach(function (panel) {
      var handle = panel.dataset.navHandle;
      var child = panel.firstElementChild;
      if (handle && child) panelCache.set(handle, child);
    });
  }

  // True only if every mega nav item in the header has a matching cached panel.
  function panelsMatchHeader() {
    var els = document.querySelectorAll(NAV_SELECTOR + ' [data-nav-handle]');
    for (var i = 0; i < els.length; i += 1) {
      if (!panelCache.has(els[i].dataset.navHandle)) return false;
    }
    return true;
  }

  // Resolve to the section HTML string, or null if it's missing/empty.
  // allowEarly=false forces a fresh network fetch, bypassing window.__lazyNavFetch.
  function requestPanels(allowEarly) {
    var earlyFetch = allowEarly && window.__lazyNavFetch;
    var p = earlyFetch
      ? earlyFetch
      : fetch('/?sections=' + SECTION_NAME, {
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        });

    return p
      .then(function (res) {
        if (!res.ok) throw new Error('nav-dropdown fetch ' + res.status);
        return res.json();
      })
      .then(function (json) {
        return json[SECTION_NAME] || null;
      });
  }

  function fetchPanels() {
    if (fetchPromise) return fetchPromise;

    fetchPromise = requestPanels(true)
      // Treat an early-fetch rejection (HTTP error / network failure) the same
      // as null so both funnel through the single retry below.
      .catch(function () { return null; })
      .then(function (html) {
        // Null panels (empty section, or a bad early fetch) — retry once with a
        // fresh network fetch before giving up.
        return html || requestPanels(false);
      })
      .then(function (html) {
        if (!html) throw new Error('nav-dropdown not in response');
        parseHtml(html);
        try { sessionStorage.setItem(SESSION_KEY, html); } catch(e) {}
      });

    return fetchPromise;
  }

  function injectPanel(navItem) {
    var handle = navItem.dataset.navHandle;
    if (!handle) return;
    var ph = navItem.querySelector(PLACEHOLDER_SELECTOR);
    if (!ph) return;

    if (panelCache.has(handle)) {
      // Replace the placeholder with the panel's inner content (.container /
      // .child-nav) so the result matches the eagerly-rendered .main-nav__child.
      ph.replaceWith(panelCache.get(handle).cloneNode(true));
    } else {
      console.warn('[lazy-nav] no panel for "' + handle + '"');
      ph.remove();
    }
  }

  // Inject all panels at once — called as soon as data is available.
  function injectAll() {
    document.querySelectorAll(NAV_SELECTOR + ' [data-nav-handle]').forEach(injectPanel);
  }

  /**
   * Replicates MainMenu.handleSidenavMenuToggle for injected .js-sidebar-hover
   * elements (MainMenu binds these at init, so lazily-injected ones are missed).
   * Delegated capture so it fires for any element whenever injected.
   */
  function handleSidebarHover(evt) {
    var el = evt.target;
    if (!el.matches || !el.matches('.js-sidebar-hover')) return;

    var container = el.closest('.child-nav');
    if (!container) return;

    var last = container.querySelector('.is-visible');
    if (last) last.classList.remove('is-visible');
    el.classList.add('is-visible');

    // Maintain the --sidebar-height CSS var from the active panel (matches
    // MainMenu.handleSidenavMenuToggle in main.js).
    var panel = el.querySelector('.child-nav__panel');
    if (panel) {
      container.style.setProperty(
        '--sidebar-height',
        Math.round(panel.getBoundingClientRect().height) + 'px'
      );
    }
  }

  function init() {
    var nav = document.querySelector(NAV_SELECTOR);
    if (!nav) return;

    nav.addEventListener('mouseenter', handleSidebarHover, true);

    if (window.__lazyNavHtml) {
      // sessionStorage hit — parse, validate against the live header, inject.
      try { parseHtml(window.__lazyNavHtml); } catch(e) {}

      if (panelsMatchHeader()) {
        injectAll();
        return;
      }

      // Stale or wrong cache (menu changed, old deploy) — discard and refetch.
      panelCache.clear();
      try { sessionStorage.removeItem(SESSION_KEY); } catch(e) {}
    }

    // Kick off / reuse the early fetch, inject all panels when it resolves
    fetchPanels()
      .then(injectAll)
      .catch(function () {
        nav.querySelectorAll(PLACEHOLDER_SELECTOR).forEach(function (ph) { ph.remove(); });
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
